import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Mic,
  MicVocal,
  MonitorDot,
  Sparkles,
  Video,
  Link as LinkIcon,
  Volume2,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ActionType } from '../../../../types';
import UploadComponent from '../../../upload';
import { RouteParams } from '../../types';
import { pushModal } from '../../../globalModals/globalModalsSlice';
import { openDictationModal } from '../../../dictation/dictationSlice';
import { RootState } from '../../../../reducers';
import { intents } from './intents';
import { trackMediaAction } from './mediaActionsSlice';
import { useUserTags } from '../../../../hooks/useTags';
import { getActiveLiveUploads } from '../../../live/liveUploadService';
import { ActiveLiveUploadResponse } from '../../../live/types';

type LibraryActionKeyType =
  | 'upload'
  | 'record-audio'
  | 'record-tab-audio'
  | 'record-screen'
  | 'import-from-url'
  | 'record-video'
  | 'instant-meeting'
  | 'new-dictation'
  | 'text-to-speech';

interface ExtendedActionType<T> extends ActionType<T> {
  tooltip: string;
  comingSoon?: boolean;
}

type MediaActionType = ExtendedActionType<LibraryActionKeyType>;

const desktopActions: MediaActionType[] = [
  {
    key: 'import-from-url',
    label: 'Import URL',
    tooltip: 'Add media from an online source',
    startIcon: <LinkIcon />,
  },
  {
    key: 'record-screen',
    label: 'Record Screen',
    tooltip: 'Record screen activity for meetings or presentations',
    startIcon: <MonitorDot />,
  },
  {
    key: 'record-audio',
    label: 'Record Microphone',
    tooltip: 'Record audio for meetings, lectures, or interviews',
    startIcon: <Mic />,
  },
  {
    key: 'new-dictation',
    label: 'Dictate',
    tooltip: 'Quickly transcribe audio, ask a question',
    startIcon: <MicVocal />,
  },
  {
    key: 'record-tab-audio',
    label: 'Record Tab Audio',
    tooltip: 'Record from Browser tab',
    startIcon: <Volume2 />,
  },
  {
    key: 'instant-meeting',
    label: 'Instant Meeting',
    tooltip: 'Video call with auto-recording to your library',
    startIcon: <Video />,
  },
];

const mobileActions: MediaActionType[] = [
  {
    key: 'record-audio',
    label: 'Record Microphone',
    tooltip: 'Record audio for meetings, lectures, or interviews',
    startIcon: <Mic />,
  },
  {
    key: 'new-dictation',
    label: 'Dictate',
    tooltip: 'Quickly transcribe audio, ask a question',
    startIcon: <MicVocal />,
  },
  {
    key: 'import-from-url',
    label: 'Import URL',
    tooltip: 'Add media from an online source',
    startIcon: <LinkIcon />,
  },
];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 600);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// Sortable Upload Component
const SortableUpload: React.FC<{ isDragging?: boolean }> = ({
  isDragging = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: 'upload',
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
    cursor: isSortableDragging ? 'grabbing' : 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col items-center min-w-[130px] flex-shrink-0"
      {...attributes}
      {...listeners}
    >
      <UploadComponent />
    </div>
  );
};

// Sortable Intent Component
interface SortableIntentProps {
  intent: any;
  onClick: () => void;
  isDragging?: boolean;
}

const SortableIntent: React.FC<SortableIntentProps> = ({
  intent,
  onClick,
  isDragging = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: 'intent',
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
    cursor: isSortableDragging ? 'grabbing' : 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col items-center min-w-[130px] flex-shrink-0 ml-1"
    >
      <Button
        onClick={onClick}
        variant="default"
        className="w-[120px] h-[75px] rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-all duration-[400ms] ease-in-out transform hover:scale-[1.02] flex flex-col items-center justify-center gap-1.5 px-3 group"
        {...attributes}
        {...listeners}
      >
        {intent?.icon ? (
          <intent.icon
            size={24}
            className="w-6 h-6 transition-transform duration-[400ms] ease-in-out group-hover:scale-110"
            style={{ width: '24px', height: '24px' }}
          />
        ) : (
          <Sparkles
            size={24}
            className="w-6 h-6 transition-transform duration-[400ms] ease-in-out group-hover:scale-110"
            style={{ width: '24px', height: '24px' }}
          />
        )}
        <span className="text-xs font-medium whitespace-nowrap transition-all duration-[400ms] ease-in-out">
          {intent?.label || intent?.key}
        </span>
      </Button>
    </div>
  );
};

// Sortable Action Component
interface SortableActionProps {
  action: MediaActionType;
  onAction: (key: LibraryActionKeyType) => void;
  isDragging?: boolean;
  liveRecording?: ActiveLiveUploadResponse | null;
}

const SortableAction: React.FC<SortableActionProps> = ({
  action,
  onAction,
  isDragging = false,
  liveRecording,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: action.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
    cursor: isSortableDragging ? 'grabbing' : 'grab',
  };

  // Check if this action should show the live recording indicator
  // Audio-only profiles: mp4a, mp3, m4a, wav, ogg
  const isAudioOnlyProfile =
    liveRecording?.profile &&
    ['mp4a', 'mp3', 'm4a', 'wav', 'ogg'].includes(liveRecording.profile);
  const showIndicator =
    liveRecording &&
    ((action.key === 'record-audio' && isAudioOnlyProfile) ||
      (action.key === 'record-screen' && !isAudioOnlyProfile));

  // Map action keys to feature IDs for onboarding tooltips
  const featureIdMap: Record<string, string> = {
    'import-from-url': 'feature-import-url',
    'record-audio': 'feature-record-audio',
    'new-dictation': 'feature-new-dictation',
  };
  const featureId = featureIdMap[action.key];

  return (
    <div
      ref={setNodeRef}
      style={style}
      id={featureId}
      className="flex flex-col items-center min-w-[130px] flex-shrink-0"
    >
      <Button
        onClick={() => onAction(action.key)}
        variant="ghost"
        className="w-[120px] h-[75px] rounded-lg bg-white dark:bg-secondary border border-gray-100 dark:border-border text-gray-700 dark:text-gray-200 hover:bg-blue-500 hover:text-white hover:border-transparent transition-all duration-[400ms] ease-in-out transform hover:scale-[1.02] flex flex-col items-center justify-center gap-1.5 px-3 group relative"
        {...attributes}
        {...listeners}
      >
        {showIndicator && (
          <Badge className="absolute top-1 right-1 h-2 w-2 p-0 bg-red-500 hover:bg-red-500 border-0" />
        )}
        {React.cloneElement(action.startIcon as React.ReactElement, {
          size: 24,
          className:
            'w-6 h-6 transition-transform duration-[400ms] ease-in-out group-hover:scale-110',
          style: { width: '24px', height: '24px' },
        })}
        <span className="text-xs font-medium whitespace-nowrap transition-all duration-[400ms] ease-in-out">
          {action.label}
        </span>
      </Button>
    </div>
  );
};

const MediaActions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamId, folderId } = useParams<RouteParams>();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [liveRecording, setLiveRecording] =
    React.useState<ActiveLiveUploadResponse | null>(null);
  const { getTag, setTag } = useUserTags();
  const { status } = useSelector((state: RootState) => ({
    status: state.import.status as 'uploading' | 'idle' | 'error' | 'success',
  }));

  // Fetch active live recordings
  React.useEffect(() => {
    let isActive = true;

    const fetchLiveRecordings = async () => {
      try {
        const recordings = await getActiveLiveUploads();
        if (isActive && recordings.length > 0) {
          setLiveRecording(recordings[0]); // Take the first active recording
        }
      } catch (error) {
        console.error('Failed to fetch active live recordings:', error);
      }
    };

    fetchLiveRecordings();

    return () => {
      isActive = false;
    };
  }, []);

  const intent = useSelector((state: RootState) => state.plan.marketing.intent);
  const isMobile = useIsMobile();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: isMobile ? 10 : 8, // Require more movement on mobile
        delay: isMobile ? 250 : 150, // Add delay on both mobile and desktop to prevent accidental drags
        tolerance: isMobile ? 5 : 0, // Allow 5px movement tolerance on mobile
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const translatedDesktopActions = React.useMemo(
    () =>
      desktopActions.map((action) => ({
        ...action,
        label: t(`mediaActions.actions.${action.key}.label`, action.label),
        tooltip: t(
          `mediaActions.actions.${action.key}.tooltip`,
          action.tooltip,
        ),
      })),
    [t],
  );

  const translatedMobileActions = React.useMemo(
    () =>
      mobileActions.map((action) => ({
        ...action,
        label: t(`mediaActions.actions.${action.key}.label`, action.label),
        tooltip: t(
          `mediaActions.actions.${action.key}.tooltip`,
          action.tooltip,
        ),
      })),
    [t],
  );

  const currentIntent = React.useMemo(
    () => (intent ? intents.find((i) => i.key === intent) : null),
    [intent],
  );

  // Create a combined list of all items (intent, upload, and actions)
  const allItems = React.useMemo(() => {
    const baseActions = isMobile
      ? translatedMobileActions
      : translatedDesktopActions;

    // Get saved order from tags
    const savedOrderTag = getTag('media-actions-order' as any);
    let savedOrder: string[] | null = null;

    try {
      savedOrder = savedOrderTag ? JSON.parse(savedOrderTag as string) : null;
    } catch (e) {
      console.error('Failed to parse media-actions-order:', e);
    }

    // Create list of all items with their keys
    const items: Array<{
      key: string;
      type: 'intent' | 'upload' | 'action';
      data?: any;
    }> = [];

    // Add intent if present
    if (currentIntent) {
      items.push({ key: 'intent', type: 'intent', data: currentIntent });
    }

    // Add upload
    items.push({ key: 'upload', type: 'upload' });

    // Add all actions
    baseActions.forEach((action) => {
      items.push({ key: action.key, type: 'action', data: action });
    });

    if (savedOrder && Array.isArray(savedOrder)) {
      // Sort items based on saved order
      const orderMap = new Map(
        savedOrder.map((key: string, index: number) => [key, index]),
      );
      items.sort((a, b) => {
        const aIndex =
          orderMap.get(a.key) ?? 999 + items.findIndex((i) => i.key === a.key);
        const bIndex =
          orderMap.get(b.key) ?? 999 + items.findIndex((i) => i.key === b.key);
        return aIndex - bIndex;
      });
    }

    return items;
  }, [
    isMobile,
    translatedDesktopActions,
    translatedMobileActions,
    getTag,
    currentIntent,
  ]);

  const handleAction = React.useCallback(
    async (
      actionKey: LibraryActionKeyType,
      options?: { dictationMode?: 'fast' | 'best' | 'answer' },
    ) => {
      console.log('handleAction called with:', actionKey, {
        teamId,
        folderId,
        status,
      });
      if (status === 'uploading') return;

      // Check if this is a recording action and there's an active recording
      if (
        liveRecording &&
        (actionKey === 'record-screen' || actionKey === 'record-audio')
      ) {
        const targetFolderId = liveRecording.folderId || folderId;
        navigate(
          `/live/${liveRecording.teamId}/${targetFolderId}/${liveRecording.liveUploadId}`,
        );
        return;
      }

      if (teamId && folderId) {
        dispatch(
          trackMediaAction({
            actionKey: actionKey.toString(),
            teamId,
            folderId,
          }),
        );
      }

      if (actionKey === 'record-audio') {
        console.log('Record audio clicked', { teamId, folderId });
        if (teamId && folderId) {
          console.log('Navigating to live with audio recording config');
          navigate(`/live/${teamId}/${folderId}`, {
            state: {
              autoStart: true,
              recordingMode: 'audio',
              settings: {
                audioSettings: {
                  microphoneAudio: true,
                  browserAudio: true,
                  isMicrophoneOnly: true,
                },
                videoSettings: {
                  screen: false,
                  webCam: false,
                },
              },
            },
          });
        } else {
          console.log('Missing teamId or folderId', { teamId, folderId });
        }
      } else if (actionKey === 'record-tab-audio') {
        console.log('Record tab audio clicked', { teamId, folderId });
        if (teamId && folderId) {
          console.log('Navigating to live with tab audio recording config');
          navigate(`/live/${teamId}/${folderId}`, {
            state: {
              autoStart: true,
              recordingMode: 'tab-audio',
              settings: {
                audioSettings: {
                  microphoneAudio: true,
                  browserAudio: true,
                  isTabAudioOnly: true,
                },
                videoSettings: {
                  screen: true,
                  webCam: false,
                },
              },
            },
          });
        } else {
          console.log('Missing teamId or folderId', { teamId, folderId });
        }
      } else if (actionKey === 'record-screen') {
        console.log('Record screen clicked', { teamId, folderId });
        if (teamId && folderId) {
          console.log('Navigating to live with screen recording config');
          navigate(`/live/${teamId}/${folderId}`, {
            state: {
              autoStart: true,
              recordingMode: 'screen',
              autoEnableScreenShare: true,
              settings: {
                audioSettings: {
                  microphoneAudio: true,
                  browserAudio: true,
                },
                videoSettings: {
                  screen: true,
                  webCam: true,
                },
              },
            },
          });
        } else {
          console.log('Missing teamId or folderId', { teamId, folderId });
        }
      } else if (actionKey === 'import-from-url') {
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          }),
        );
      } else if (actionKey === 'instant-meeting') {
        navigate('/tool/p2pmeeting', { state: { fromMediaActions: true } });
      } else if (actionKey === 'new-dictation') {
        if (options?.dictationMode) {
          dispatch(openDictationModal(options.dictationMode));
        } else {
          dispatch(openDictationModal());
        }
      } else if (actionKey === 'text-to-speech') {
        dispatch(
          pushModal({
            type: 'text-to-speech-modal',
          }),
        );
      }
    },
    [dispatch, status, teamId, folderId, navigate, liveRecording],
  );

  const handleIntentClick = React.useCallback(() => {
    if (!currentIntent?.action) return;

    const options = currentIntent.dictationMode
      ? { dictationMode: currentIntent.dictationMode }
      : undefined;

    handleAction(currentIntent.action as LibraryActionKeyType, options);
  }, [currentIntent, handleAction]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = allItems.findIndex((item) => item.key === active.id);
      const newIndex = allItems.findIndex((item) => item.key === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create new order array
        const newOrder = [...allItems];
        const [movedItem] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, movedItem);

        // Save new order to tags
        const orderKeys = newOrder.map((item) => item.key);
        setTag({
          key: 'media-actions-order' as any,
          value: JSON.stringify(orderKeys),
        });
      }
    }

    setActiveId(null);
  };

  const activeItem = activeId
    ? allItems.find((item) => item.key === activeId)
    : null;

  const checkScroll = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  React.useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => container.removeEventListener('scroll', checkScroll);
    }
    return undefined;
  }, [checkScroll, allItems.length]);

  React.useEffect(() => {
    const handleResize = () => checkScroll();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkScroll]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        className={cn('w-full', 'flex items-center justify-center', 'relative')}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div
          ref={scrollContainerRef}
          className="w-full overflow-x-auto scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <div
            className={cn(
              'flex gap-1',
              'w-max', // Always left-aligned
              'py-1',
              'pl-2', // Align with library content
            )}
          >
            <SortableContext
              items={allItems.map((item) => item.key)}
              strategy={horizontalListSortingStrategy}
            >
              {allItems.map((item) => {
                if (item.type === 'intent' && item.data) {
                  return (
                    <SortableIntent
                      key={item.key}
                      intent={item.data}
                      onClick={handleIntentClick}
                      isDragging={item.key === activeId}
                    />
                  );
                }
                if (item.type === 'upload') {
                  return (
                    <SortableUpload
                      key={item.key}
                      isDragging={item.key === activeId}
                    />
                  );
                }
                if (item.type === 'action' && item.data) {
                  return (
                    <SortableAction
                      key={item.key}
                      action={item.data}
                      onAction={handleAction}
                      isDragging={item.key === activeId}
                      liveRecording={liveRecording}
                    />
                  );
                }
                return null;
              })}
            </SortableContext>
            {/* Right spacer for scrolling space */}
            <div className="w-1 flex-shrink-0" />
          </div>
        </div>

        {/* Left Chevron */}
        <Button
          onClick={scrollLeft}
          size="icon"
          variant="ghost"
          className={cn(
            'absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 dark:bg-secondary/90 backdrop-blur-sm border border-gray-100 dark:border-border hover:bg-blue-500 hover:text-white hover:border-transparent',
            'transition-all duration-[400ms] ease-in-out transform hover:scale-110',
            canScrollLeft && isHovering
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 -translate-x-4 pointer-events-none',
          )}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {/* Right Chevron */}
        <Button
          onClick={scrollRight}
          size="icon"
          variant="ghost"
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 dark:bg-secondary/90 backdrop-blur-sm border border-gray-100 dark:border-border hover:bg-blue-500 hover:text-white hover:border-transparent',
            'transition-all duration-[400ms] ease-in-out transform hover:scale-110',
            canScrollRight && isHovering
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-4 pointer-events-none',
          )}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <DragOverlay>
        {activeItem && (
          <div className="flex flex-col items-center min-w-[130px]">
            {(() => {
              if (activeItem.type === 'intent' && activeItem.data) {
                return (
                  <Button
                    variant="default"
                    className="w-[120px] h-[75px] rounded-lg bg-blue-500 text-white flex flex-col items-center justify-center gap-1.5 px-3 shadow-xl"
                  >
                    {activeItem.data.icon ? (
                      <activeItem.data.icon
                        size={24}
                        className="w-6 h-6"
                        style={{ width: '24px', height: '24px' }}
                      />
                    ) : (
                      <Sparkles
                        size={24}
                        className="w-6 h-6"
                        style={{ width: '24px', height: '24px' }}
                      />
                    )}
                    <span className="text-xs font-medium whitespace-nowrap">
                      {activeItem.data.label || activeItem.data.key}
                    </span>
                  </Button>
                );
              }

              if (activeItem.type === 'upload') {
                return (
                  <Button
                    variant="ghost"
                    className="w-[120px] h-[75px] rounded-lg bg-white dark:bg-secondary border border-gray-100 dark:border-border text-gray-700 dark:text-gray-200 flex flex-col items-center justify-center gap-1.5 px-3 shadow-xl"
                  >
                    <CloudUpload
                      size={24}
                      className="w-6 h-6"
                      style={{ width: '24px', height: '24px' }}
                    />
                    <span className="text-xs font-medium whitespace-nowrap">
                      Upload
                    </span>
                  </Button>
                );
              }

              if (activeItem.type === 'action' && activeItem.data) {
                return (
                  <Button
                    variant="ghost"
                    className="w-[120px] h-[75px] rounded-lg bg-white dark:bg-secondary border border-gray-100 dark:border-border text-gray-700 dark:text-gray-200 flex flex-col items-center justify-center gap-1.5 px-3 shadow-xl"
                  >
                    {React.cloneElement(
                      activeItem.data.startIcon as React.ReactElement,
                      {
                        size: 24,
                        className: 'w-6 h-6',
                        style: { width: '24px', height: '24px' },
                      },
                    )}
                    <span className="text-xs font-medium whitespace-nowrap">
                      {activeItem.data.label}
                    </span>
                  </Button>
                );
              }

              return null;
            })()}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default React.memo(MediaActions);
